import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AttributeKindEnum,
  CpLevelEnum,
  cpLevelEnum,
  TemplateAttributeRole,
  type BatchTypeEnum,
  type HarvestOperationTemplateAttribute,
} from "@brewbase/db/types";
import InlineEditableInput from "@/components/InlineEditableInput";
import { TargetIcon } from "lucide-react";
import { AppliesToScalesField } from "./applies-to-scales-field";
import { CpSpecEditor } from "@/components/cp-spec-editor";
import {
  SectionHeader,
  FlagBox,
  DefaultValuesContent,
} from "@/features/template-block-editor/inspector-helpers";
import type { InspectorExtrasArgs } from "@/features/template-block-editor/types";

export function OperationInspectorExtras({
  attribute: baseAttribute,
  isLocked,
  onUpdateSettings,
  availableBatchTypes,
}: InspectorExtrasArgs & { availableBatchTypes: BatchTypeEnum[] }) {
  const attribute = baseAttribute as HarvestOperationTemplateAttribute;
  const isParameter = attribute.role === TemplateAttributeRole.PARAMETER;
  const isControlPoint = attribute.is_control_point ?? false;
  const kind = attribute.kind as AttributeKindEnum;

  return (
    <>
      {/* Flags */}
      <SectionHeader title="Flags" />
      <div className="px-3 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FlagBox
            id="is-control-point"
            icon={<TargetIcon className="h-3 w-3 text-muted-foreground" />}
            label="Control Point"
            description="Define limits and checks."
            checked={isControlPoint}
            disabled={isLocked}
            onChange={(checked) => {
              onUpdateSettings({
                is_control_point: checked,
                ...(!checked && {
                  cp_level: null,
                  cp_details: null,
                  cp_default_number_target: null,
                  cp_default_number_rel_tol_upper: null,
                  cp_default_number_rel_tol_lower: null,
                  cp_default_number_abs_tol_upper: null,
                  cp_default_number_abs_tol_lower: null,
                  cp_default_number_unit: null,
                  cp_default_check_value: null,
                }),
              });
            }}
          />
        </div>
      </div>

      {/* Applies to scales — conditional display. Empty = every scale. */}
      <SectionHeader title="Applies to scales" />
      <div className="px-3 py-3 space-y-1">
        <AppliesToScalesField
          value={attribute.applicable_scale_ids}
          onChange={(ids) =>
            onUpdateSettings({ applicable_scale_ids: ids })
          }
          disabled={isLocked}
        />
      </div>

      {/* Parameter Defaults */}
      {isParameter && (
        <>
          <SectionHeader title="Parameter Defaults" />
          <div className="px-3 py-3">
            <DefaultValuesContent
              kind={kind}
              attribute={attribute}
              onUpdateSettings={onUpdateSettings}
            />
          </div>
        </>
      )}

      {/* Control Point */}
      {isControlPoint && (
        <>
          <SectionHeader title="Control Point" />
          <div className="px-3 py-3">
            <ControlPointContent
              kind={kind}
              attribute={attribute}
              isLocked={isLocked}
              onUpdateSettings={onUpdateSettings}
            />
          </div>
        </>
      )}

      {/* Mass Balance — only valid on numeric kinds (DB constraint) */}
    </>
  );
}

// ======================== Control Point ========================

function ControlPointContent({
  kind,
  attribute,
  isLocked,
  onUpdateSettings,
}: {
  kind?: AttributeKindEnum;
  attribute: HarvestOperationTemplateAttribute;
  isLocked: boolean;
  onUpdateSettings: (settings: Record<string, unknown>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-6 gap-2">
        <div className="col-span-2">
          <Label className="text-xs font-medium">Level</Label>
          <Select
            value={(attribute.cp_level as string) || ""}
            onValueChange={(value) =>
              onUpdateSettings({
                is_control_point: true,
                cp_level: value as CpLevelEnum,
              })
            }
            disabled={isLocked}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {Object.values(cpLevelEnum.enum).map((lvl) => (
                <SelectItem key={lvl} value={lvl}>
                  {lvl}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-4">
          <Label className="text-xs font-medium">Details</Label>
          <InlineEditableInput
            value={attribute.cp_details || ""}
            onSave={(value) =>
              onUpdateSettings({
                is_control_point: true,
                cp_details: value,
              })
            }
            placeholder="Control point details..."
          />
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium mb-2">Acceptance</Label>
        <CpSpecEditor
          kind={kind ?? AttributeKindEnum.SINGLE_NUMBER}
          idPrefix={`tmpl-cp-${attribute.id}`}
          disabled={isLocked}
          window={{
            target: attribute.cp_default_number_target ?? null,
            absLower: attribute.cp_default_number_abs_tol_lower ?? null,
            absUpper: attribute.cp_default_number_abs_tol_upper ?? null,
            relLower: attribute.cp_default_number_rel_tol_lower ?? null,
            relUpper: attribute.cp_default_number_rel_tol_upper ?? null,
          }}
          unit={attribute.cp_default_number_unit ?? null}
          checkValue={attribute.cp_default_check_value ?? null}
          onWindowChange={(w) =>
            onUpdateSettings({
              is_control_point: true,
              cp_default_number_target: w.target,
              cp_default_number_abs_tol_lower: w.absLower,
              cp_default_number_abs_tol_upper: w.absUpper,
              cp_default_number_rel_tol_lower: w.relLower,
              cp_default_number_rel_tol_upper: w.relUpper,
            })
          }
          onUnitChange={(u) =>
            onUpdateSettings({
              is_control_point: true,
              cp_default_number_unit: u,
            })
          }
          onCheckChange={(v) =>
            onUpdateSettings({
              is_control_point: true,
              cp_default_check_value: v,
            })
          }
        />
      </div>
    </div>
  );
}
